import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
/* ================= INITIAL STATE ================= */
const initialState = {
    goals: [],
    loading: false,
    syncing: false,
    error: null,
    year_month: new Date().toISOString().slice(0, 7), // Default to current month
    goal_type: "projection_goal",
    page: 1,
    limit: 20,
    total: 0,
    lastUpdated: null,
    actionLogs: [],
    actionLogsLoading: false,
    sort_by: null,
    sort_order: null,
    service_type: '',
    search_term: '',
};
/* ================= ASYNC THUNKS ================= */
export const fetchGoals = createAsyncThunk("goals/fetchGoals", async ({ year_month, goal_type, page, limit, am_id, sort_by, sort_order, service_type, search_term }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        let url = `/api/goals?year_month=${year_month}&goal_type=${goal_type}&page=${page}&limit=${limit}`;
        if (am_id)
            url += `&am_id=${am_id}`;
        if (sort_by)
            url += `&sort_by=${sort_by}`;
        if (sort_order)
            url += `&sort_order=${sort_order}`;
        if (service_type)
            url += `&service_type=${service_type}`;
        if (search_term)
            url += `&search_term=${encodeURIComponent(search_term)}`;
        const res = await ApiCall("GET", url, null, { Authorization: `Bearer ${token}`, 'x-module-key': 'client_goal' });
        if (res && "data" in res && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch goals");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch goals error");
    }
});
export const syncGoals = createAsyncThunk("goals/syncGoals", async ({ month, notify = false }, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/goals/sync`, { month, notify }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Goals synced successfully");
            dispatch(fetchGoals({
                year_month: month,
                goal_type: getState().goals.goal_type,
                page: getState().goals.page,
                limit: getState().goals.limit,
                am_id: getState().goals.am_id, // Note: am_id isn't in slice state yet, but used in component. 
                sort_by: getState().goals.sort_by,
                sort_order: getState().goals.sort_order,
                service_type: getState().goals.service_type,
                search_term: getState().goals.search_term
            }));
            return;
        }
        const msg = res?.data?.message || "Failed to sync goals";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Sync goals failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const updateGoalStatus = createAsyncThunk("goals/updateGoalStatus", async ({ year_month, client_ids, am_id }, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/goals/update-am-projection`, { year_month, client_ids, am_id }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Goals submitted for approval.");
            dispatch(fetchGoals({
                year_month,
                goal_type: getState().goals.goal_type,
                page: getState().goals.page,
                limit: getState().goals.limit,
                am_id: am_id,
                sort_by: getState().goals.sort_by,
                sort_order: getState().goals.sort_order,
                service_type: getState().goals.service_type,
                search_term: getState().goals.search_term
            }));
            return;
        }
        const msg = res?.data?.message || "Failed to update status";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Update status failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const saveGoalActionLog = createAsyncThunk("goals/saveGoalActionLog", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/approval-activity`, {
            ...payload,
            send_by: "user",
            status: "pending"
        }, {
            Authorization: `Bearer ${token}`,
            'x-module-key': 'goal_management'
        });
        if (res && "data" in res && res.data?.success) {
            toast.success("Notes saved successfully");
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to save log");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Save log error");
    }
});
export const fetchGoalActionLogs = createAsyncThunk("goals/fetchGoalActionLogs", async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const { campaign_id, activity_type, month_label } = params;
        let url = `/api/approval-activity?activity_type=${activity_type}`;
        if (campaign_id)
            url += `&campaign_id=${campaign_id}`;
        if (month_label)
            url += `&month_label=${encodeURIComponent(month_label)}`;
        const res = await ApiCall("GET", url, null, {
            Authorization: `Bearer ${token}`,
            'x-module-key': 'goal_management'
        });
        if (res && "data" in res && res.data?.success) {
            return res.data.data;
        }
        return rejectWithValue(res?.data?.message || "Failed to fetch logs");
    }
    catch (err) {
        return rejectWithValue(err?.message || "Fetch logs error");
    }
});
export const updateClientGoals = createAsyncThunk("goals/updateClientGoals", async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/goals/update-client-goals`, payload, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Goals updated successfully");
            await dispatch(fetchGoals({
                year_month: payload.year_month,
                goal_type: getState().goals.goal_type,
                page: getState().goals.page,
                limit: getState().goals.limit,
                am_id: getState().goals.am_id,
                sort_by: getState().goals.sort_by,
                sort_order: getState().goals.sort_order,
                service_type: getState().goals.service_type,
                search_term: getState().goals.search_term
            }));
            return;
        }
        const msg = res?.data?.message || "Failed to update goals";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Update goals failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const approvePendingGoals = createAsyncThunk("goals/approvePendingGoals", async ({ year_month, client_ids }, { getState, dispatch, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/goals/pending-approval-clients`, { year_month, client_ids }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "All pending goals approved successfully");
            // Refetch goals after approval
            dispatch(fetchGoals({
                year_month,
                goal_type: getState().goals.goal_type,
                page: getState().goals.page,
                limit: getState().goals.limit,
                am_id: getState().goals.am_id,
                sort_by: getState().goals.sort_by,
                sort_order: getState().goals.sort_order,
                service_type: getState().goals.service_type,
                search_term: getState().goals.search_term
            }));
            return;
        }
        const msg = res?.data?.message || "Failed to approve pending goals";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.message || "Approve pending goals failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
const goalSlice = createSlice({
    name: "goals",
    initialState,
    reducers: {
        setYearMonth: (state, action) => {
            state.year_month = action.payload;
            state.page = 1; // Reset page on month change
        },
        setGoalType: (state, action) => {
            state.goal_type = action.payload;
            state.page = 1; // Reset page on type change
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setLimit: (state, action) => {
            state.limit = action.payload;
            state.page = 1; // Reset page on limit change
        },
        setSortConfig: (state, action) => {
            state.sort_by = action.payload.sort_by;
            state.sort_order = action.payload.sort_order;
            state.page = 1;
        },
        setServiceType: (state, action) => {
            state.service_type = action.payload;
            state.page = 1;
        },
        setSearchTerm: (state, action) => {
            state.search_term = action.payload;
            state.page = 1;
        },
        clearGoalsError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Goals
            .addCase(fetchGoals.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchGoals.fulfilled, (state, action) => {
            state.loading = false;
            const data = action.payload;
            state.goals = data?.result || (Array.isArray(data) ? data : []);
            state.total = data?.pagination?.total || (Array.isArray(data) ? data.length : 0);
            state.lastUpdated = data?.lastUpdated || new Date().toISOString();
        })
            .addCase(fetchGoals.rejected, (state, action) => {
            if (action.meta.aborted) {
                return;
            }
            state.loading = false;
            state.error = action.payload || "An error occurred while fetching goals";
            state.goals = [];
        })
            // Sync Goals
            .addCase(syncGoals.pending, (state) => {
            state.syncing = true;
        })
            .addCase(syncGoals.fulfilled, (state) => {
            state.syncing = false;
            // fetchGoals is dispatched afterwards and will update lastUpdated
        })
            .addCase(syncGoals.rejected, (state) => {
            state.syncing = false;
        })
            // Update Client Goals
            .addCase(updateClientGoals.pending, (state) => {
            state.loading = true;
        })
            .addCase(updateClientGoals.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(updateClientGoals.rejected, (state) => {
            state.loading = false;
        })
            // Action Logs
            .addCase(fetchGoalActionLogs.pending, (state) => {
            state.actionLogsLoading = true;
        })
            .addCase(fetchGoalActionLogs.fulfilled, (state, action) => {
            state.actionLogsLoading = false;
            state.actionLogs = action.payload;
        })
            .addCase(fetchGoalActionLogs.rejected, (state) => {
            state.actionLogsLoading = false;
            state.actionLogs = [];
        })
            .addCase(saveGoalActionLog.fulfilled, (state) => {
            state.actionLogsLoading = false;
        });
    },
});
/* ================= SELECTORS ================= */
export const { setYearMonth, setGoalType, setPage, setLimit, setSortConfig, setServiceType, setSearchTerm, clearGoalsError } = goalSlice.actions;
export const selectGoals = (state) => state.goals.goals;
export const selectGoalsLoading = (state) => state.goals.loading;
export const selectGoalsSyncing = (state) => state.goals.syncing;
export const selectGoalsError = (state) => state.goals.error;
export const selectYearMonth = (state) => state.goals.year_month;
export const selectGoalType = (state) => state.goals.goal_type;
export const selectPage = (state) => state.goals.page;
export const selectLimit = (state) => state.goals.limit;
export const selectTotalGoals = (state) => state.goals.total;
export const selectLastUpdated = (state) => state.goals.lastUpdated;
export const selectActionLogs = (state) => state.goals.actionLogs;
export const selectActionLogsLoading = (state) => state.goals.actionLogsLoading;
export const selectSortBy = (state) => state.goals.sort_by;
export const selectSortOrder = (state) => state.goals.sort_order;
export const selectServiceType = (state) => state.goals.service_type;
export const selectSearchTerm = (state) => state.goals.search_term;
export default goalSlice.reducer;
